import { ArrowLeft, MailCheck } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { AuthCloseButton } from "@/components/AuthCloseButton";
import { Wordmark } from "@/components/brand/Wordmark";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function ForgotPassword() {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [devUrl, setDevUrl] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: email.trim(), locale: i18n.resolvedLanguage });
      setSent(true);
      if (data.dev_reset_url) setDevUrl(data.dev_reset_url);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || t("auth.genericError"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-20" data-testid="forgot-password-page">
      <AuthCloseButton />
      <div className="w-full max-w-md flex flex-col gap-10">
        <div className="flex flex-col items-start gap-6">
          <Wordmark />
          <div>
            <h1 className="text-3xl sm:text-4xl font-medium tracking-tight text-[#0A0A0A]">{t("auth.forgotTitle")}</h1>
            <p className="mt-2 text-sm text-[#737373]">{t("auth.forgotSubtitle")}</p>
          </div>
        </div>

        {sent ? (
          <div className="flex flex-col gap-6" data-testid="forgot-password-sent">
            <div className="rounded-2xl bg-[#1D4D3B]/[0.06] border border-[#1D4D3B]/15 px-6 py-5 flex items-start gap-4">
              <MailCheck className="w-5 h-5 text-[#1D4D3B] mt-0.5 shrink-0" aria-hidden="true" />
              <div className="flex flex-col gap-1">
                <span className="text-sm font-semibold text-[#0A0A0A]">{t("auth.forgotSentTitle")}</span>
                <span className="text-sm text-[#737373]">{t("auth.forgotSentDesc")}</span>
              </div>
            </div>
            {devUrl && (
              <div className="rounded-2xl bg-amber-500/[0.06] border border-dashed border-amber-500/40 px-5 py-4 flex flex-col gap-2" data-testid="forgot-dev-panel">
                <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-700">{t("auth.devModeLabel")}</span>
                <p className="text-xs text-[#737373]">{t("auth.devModeDesc")}</p>
                <a href={devUrl} data-testid="forgot-dev-link" className="text-xs font-medium text-[#0A0A0A] underline underline-offset-4 break-all">
                  {devUrl}
                </a>
              </div>
            )}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col gap-6" noValidate>
            <div className="flex flex-col gap-2">
              <Label htmlFor="forgot-email" className="text-sm font-medium">{t("auth.email")}</Label>
              <Input
                id="forgot-email"
                data-testid="forgot-email-input"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("auth.emailPlaceholder")}
                className="h-12 rounded-xl border-black/10 focus-visible:ring-1 focus-visible:ring-[#0A0A0A] bg-transparent"
              />
            </div>
            {error && (
              <p data-testid="forgot-error" role="alert" className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
                {error}
              </p>
            )}
            <Button
              type="submit"
              data-testid="forgot-submit-button"
              disabled={submitting || !email}
              className="h-12 rounded-full bg-[#0A0A0A] text-white hover:bg-black/80 text-base transition-colors duration-300 disabled:opacity-60"
            >
              {submitting ? `${t("common.loading")}…` : t("auth.forgotBtn")}
            </Button>
          </form>
        )}

        <Link
          to="/signin"
          data-testid="forgot-back-to-signin"
          className="inline-flex items-center gap-2 text-sm font-medium text-[#0A0A0A]/60 hover:text-[#0A0A0A] transition-colors duration-200 w-fit"
        >
          <ArrowLeft className="w-4 h-4" aria-hidden="true" />
          {t("auth.backToSignIn")}
        </Link>
      </div>
    </div>
  );
}
